import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import H from '@here/maps-api-for-javascript';
import { useDispatch, useSelector } from 'react-redux';
import { last } from 'lodash';
import { getSafeValue } from 'utils/security';
import { isSegmentHidden } from 'utils/map/MapFeatureFilters';
import { getGeoInfoFromObjects, getOriginalLines } from 'utils/map/MapTourLineUtils';
import {
  getLastValidIteration,
  getToursDataFromIteration,
  isIterationSolution,
} from 'utils/SolutionHelpers';
import { tourToRoutingRequest } from 'utils/RoutingConverter';
import { getRoutingTourInfo, selectTourById, setDisplayParam } from 'actions';
import { AMPLITUDE_EVENTS, AmplitudeService } from 'utils/amplitude';
import {
  buildHitAreaPolylines,
  buildOriginalLocationPolylines,
  buildRoutingPolylines,
  buildStraightRoutePolylines,
  buildUnselectedPolylines,
} from './utils/tourLineBuilder';

const ORIGINAL_LOCATION_MIN_ZOOM = 16;
const MAX_TOURS_FOR_UNSELECTED_DISPLAY = 100;

const NewMapTourLines = ({ map, user, oAuth, mapData }) => {
  const dispatch = useDispatch();
  const solution = useSelector(({ solution: s }) => s);
  const routingData = useSelector(({ routingTour }) => routingTour);
  const display = useSelector(({ mapContainer }) => mapContainer.display);
  const tourPlanner = useSelector(({ tourPlanner: tp }) => tp.value[solution.show]);
  const orders = useSelector((state) => state.orders[solution.show]);

  const groupRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(14);

  const lastIteration = useMemo(
    () => solution && last(solution.requests[solution.show]?.iterations),
    [solution],
  );
  const isSolution = isIterationSolution(lastIteration);
  const solutionId = solution.show;
  const tempOrders = solution.requests[solution.show]?.tempOrders;

  const filtered = useMemo(
    () => getLastValidIteration(solution.requests[solution.show]),
    [solution],
  );
  const tourData = useMemo(() => getToursDataFromIteration(filtered), [filtered]);

  const fleetType = tourPlanner?.vehicleProfiles?.[0]?.fleetType;
  const { gradientRoutes, showOverlaps, hideVisited, hideAllRoutes, showStraightRouteLines } =
    user.mapSettings;

  const styleConfig = useMemo(
    () => ({ gradientRoutes, fleetType, showOverlaps }),
    [gradientRoutes, fleetType, showOverlaps],
  );

  const routingRoutes = useMemo(() => {
    const routeRequestError = routingData && routingData.requestError;
    return routeRequestError || showStraightRouteLines
      ? null
      : getGeoInfoFromObjects(routingData);
  }, [routingData, showStraightRouteLines]);

  const originalLocation = useMemo(() => {
    const routeRequestError = routingData && routingData.requestError;
    return routeRequestError ? null : getOriginalLines(routingData);
  }, [routingData]);

  const onTourLineClick = useCallback(
    (feature) => {
      const routeId = feature.properties.routeId;
      if (routeId === undefined) return;
      if (display.selectedTour !== routeId) {
        const tour = getSafeValue(tourData?.tours, routeId);
        dispatch(selectTourById({ selectedTour: routeId, noMapMovement: true }));
        dispatch(selectTourById({ selectOne: routeId, noMapMovement: true }));
        dispatch(
          getRoutingTourInfo({
            oAuth,
            routingRequest: tourToRoutingRequest(
              tour,
              tourPlanner,
              tempOrders || orders,
              null,
              hideAllRoutes,
            ),
            tourId: routeId,
            solutionId,
          }),
        );
        AmplitudeService.log(AMPLITUDE_EVENTS.MAP_MARKER_ACTIVATE_TOUR);
      }
    },
    [dispatch, display.selectedTour, oAuth, tourPlanner, orders, tempOrders, hideAllRoutes, solutionId, tourData],
  );

  const onToggleHiddenSegment = useCallback(
    (feature) => {
      const { routeId, fromStop } = feature.properties;
      if (routeId === undefined || fromStop === undefined) return;
      const current = display.hiddenSegments || [];
      const isHidden = isSegmentHidden(feature, current);
      const hiddenSegments = isHidden
        ? current.filter((s) => !(s.routeId === routeId && s.fromStop === fromStop))
        : [...current, { routeId, fromStop }];
      dispatch(setDisplayParam({ hiddenSegments }));
    },
    [display.hiddenSegments, dispatch],
  );

  const onTourLineClickRef = useRef(onTourLineClick);
  const onToggleHiddenSegmentRef = useRef(onToggleHiddenSegment);
  useEffect(() => {
    onTourLineClickRef.current = onTourLineClick;
    onToggleHiddenSegmentRef.current = onToggleHiddenSegment;
  });

  const stableOnTourLineClick = useCallback((feature) => onTourLineClickRef.current(feature), []);
  const stableOnToggleHiddenSegment = useCallback(
    (feature) => onToggleHiddenSegmentRef.current(feature),
    [],
  );

  useEffect(() => {
    if (!map) return undefined;
    groupRef.current = new H.map.Group();
    map.addObject(groupRef.current);

    const onZoom = () => {
      const z = map.getZoom();
      setZoomLevel((prev) => {
        const wasAbove = prev > ORIGINAL_LOCATION_MIN_ZOOM;
        const isAbove = z > ORIGINAL_LOCATION_MIN_ZOOM;
        return wasAbove !== isAbove ? z : prev;
      });
    };
    map.addEventListener('mapviewchange', onZoom);

    return () => {
      map.removeEventListener('mapviewchange', onZoom);
      if (groupRef.current) {
        groupRef.current.removeAll();
        map.removeObject(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [map]);

  const originalLocationGroupRef = useRef(null);

  useEffect(() => {
    if (!groupRef.current) return;
    groupRef.current.removeAll();
    originalLocationGroupRef.current = null;

    if (!isSolution || hideAllRoutes) return;

    const addAll = (polylines) => polylines.forEach((p) => groupRef.current.addObject(p));

    if (showStraightRouteLines) {
      addAll(buildStraightRoutePolylines(mapData, display, routingData, styleConfig));
      return;
    }

    if (!routingRoutes?.features) return;

    const totalTours = tourData?.tours?.length;
    const displayUnselected = totalTours != null && totalTours <= MAX_TOURS_FOR_UNSELECTED_DISPLAY;

    if (!hideVisited && displayUnselected) {
      addAll(buildUnselectedPolylines(routingRoutes, display));
    }

    addAll(buildRoutingPolylines(routingRoutes, display, routingData, styleConfig));

    const olGroup = new H.map.Group();
    groupRef.current.addObject(olGroup);
    originalLocationGroupRef.current = olGroup;

    if (zoomLevel > ORIGINAL_LOCATION_MIN_ZOOM) {
      buildOriginalLocationPolylines(originalLocation, display).forEach((p) => olGroup.addObject(p));
    }

    addAll(
      buildHitAreaPolylines(
        routingRoutes,
        display,
        routingData,
        stableOnTourLineClick,
        stableOnToggleHiddenSegment,
      ),
    );
  }, [
    routingRoutes,
    originalLocation,
    display,
    routingData,
    isSolution,
    hideAllRoutes,
    hideVisited,
    styleConfig,
    showStraightRouteLines,
    mapData,
    tourData,
    stableOnTourLineClick,
    stableOnToggleHiddenSegment,
  ]);

  useEffect(() => {
    if (!originalLocationGroupRef.current) return;
    originalLocationGroupRef.current.removeAll();
    if (zoomLevel > ORIGINAL_LOCATION_MIN_ZOOM) {
      buildOriginalLocationPolylines(originalLocation, display).forEach((p) =>
        originalLocationGroupRef.current.addObject(p),
      );
    }
  }, [zoomLevel, originalLocation, display]);

  return null;
};

export default NewMapTourLines;
